import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';

// Default blog posts (fallback if API fails)
const defaultBlogPosts = [
  {
    slug: "dubai-str-market-2026",
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800",
    category: "Market Insights",
    title: "Dubai's STR Market in 2025: What Property Owners Need to Know",
    excerpt: "Latest regulations, occupancy trends, and emerging neighborhoods driving UAE's vacation rental boom.",
    metaTitle: "Dubai STR Market 2026: Why The Rebound Is Coming",
    metaDescription: "The UAE short-term rental market took a hit. Before you panic, here's what the 2026 numbers actually show — and why a rebound is already underway.",
    content: `Dubai's short-term rental market continues to evolve rapidly, and 2026 is shaping up to be a pivotal year for property owners and operators across the UAE.

The Market Is Maturing

What we're seeing across Dubai, Abu Dhabi, and Ras Al Khaimah is a market that's moving away from the "list it and they will come" mentality. Occupancy rates are stabilizing, competition is increasing, and guests are becoming more discerning about where they stay.

This isn't bad news — it's a natural market correction that rewards operators who invest in quality, strategy, and guest experience.

Regulatory Landscape

Dubai Tourism's continued rollout of STR regulations means compliance is no longer optional. Properties must be properly licensed, and operators who haven't registered their units are finding themselves shut out of major OTAs.

The good news? Compliance creates a level playing field. Properties that were competing on price alone are being replaced by legitimate operators who understand hospitality as a business.

Emerging Neighborhoods

While Dubai Marina and Downtown remain strong performers, we're seeing increased interest in:
- Jumeirah Village Circle and Triangle
- Dubai Hills Estate
- Business Bay (particularly for corporate stays)
- Ras Al Khaimah beachfront properties

These areas offer better value per square meter and are attracting both investors and guests looking for alternatives to the premium districts.

What This Means for Operators

The operators thriving in 2026 are those who:
- Treat their STR as a business, not a side project
- Invest in professional listing optimization and photography
- Implement dynamic pricing strategies
- Focus on guest experience from pre-booking to checkout
- Stay on top of platform algorithm changes

If you're still using static pricing, recycled listing copy, or treating reviews as an afterthought, 2026 is the year to level up.

The Opportunity

Dubai's tourism sector is projected to continue growing, with visitor numbers expected to reach new highs. The opportunity is real — but it's no longer enough to simply own a property in a good location.

Success now belongs to operators who understand that short-term rentals are a hospitality business that happens to take place in residential property.

What Should You Do?

If you're serious about maximizing your property's performance in 2026:
1. Get a performance audit to understand where you stand
2. Optimize your listing for platform algorithms and guest conversion
3. Implement proper revenue management (not just calendar-based pricing)
4. Design a guest experience that drives 5-star reviews
5. Stay compliant with local regulations

The UAE STR market is evolving from Wild West to mature industry. The operators who adapt will thrive. Those who don't will wonder why their bookings are down.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "5 min read"
  },
  {
    slug: "5-star-guest-experience",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800",
    category: "Operations",
    title: "The 5-Star Guest Experience Blueprint",
    excerpt: "How top-performing UAE properties consistently deliver exceptional stays that drive repeat bookings.",
    metaTitle: "Why Your STR Isn't Getting 5 Stars — Ask The Ritz-Carlton",
    metaDescription: "A cracked cup isn't just a cracked cup. It's proof nobody checked — and guests notice everything your team might be missing.",
    content: `Every operator wants 5-star reviews. But most approach it backwards — they focus on the property itself rather than the guest journey.

Here's the truth: a perfectly designed apartment with high-end finishes can still get 3-star reviews if the guest experience is broken. And a modest property with an exceptional guest journey will consistently outperform it.

The UAE's top STR operators understand this. Here's how they do it.

It Starts Before They Arrive

The guest experience doesn't begin at check-in — it begins the moment someone views your listing.

Top performers ensure:
- Professional, high-quality photography that sets accurate expectations
- Listing copy that answers questions before they're asked
- Clear, honest descriptions of the space, location, and amenities
- Pre-booking communication that builds confidence

Once someone books, the experience intensifies. You have one chance to set the tone, and it happens between booking confirmation and arrival.

What to send:
- Personalized welcome message within 2 hours of booking
- Clear check-in instructions (sent 48 hours before arrival)
- Local recommendations tailored to their stay purpose
- Proactive communication about anything that might affect their stay

The 48 Hours Before Check-In

This is where most operators drop the ball. They assume the guest remembers the check-in instructions from the booking confirmation. They don't.

48 hours before arrival, send:
- Exact check-in location with map link
- Access codes or key collection details
- Parking instructions if applicable
- What to do if they arrive early or late
- Emergency contact details

Check-In Experience

The moment a guest unlocks your door, they're forming their impression of the stay. You have about 60 seconds to deliver on the promise your listing made.

What guests notice immediately:
- Temperature (too hot, too cold, or stuffy kills the vibe)
- Smell (this is make-or-break)
- Cleanliness (not clean enough vs hospitality-grade clean)
- First visual impression (does it match the photos?)

Top properties include:
- A welcome note (handwritten beats printed)
- A small welcome amenity (dates, Arabic coffee, local treats)
- Crystal-clear house manual (not a 10-page essay)
- Emergency contact information displayed prominently

During The Stay

The best guest experience is one where guests don't need to contact you — because everything works and everything they might need is already there.

But when they do reach out, response time matters. A lot.

In the UAE market:
- Respond within 15 minutes during waking hours
- Have a backup contact available 24/7
- Solve problems proactively (don't wait for complaints)
- Follow up without being intrusive

The Check-Out Experience

Most operators think check-out is just about getting the keys back. Top performers see it as the moment that determines whether someone leaves a review and what they say.

Make check-out easy:
- Flexible check-out time when possible (builds goodwill)
- Simple departure instructions
- A thank you message
- No aggressive cleaning demands

Then, after they leave:
- Send a thank you message within 24 hours
- Politely request a review (without begging)
- Invite them to return

What Drives Repeat Bookings

Repeat guests are gold — they book directly, they're lower risk, and they spend more per stay.

What makes guests come back:
- Consistency (the experience matches what they remember)
- Personal touches (remembering their preferences)
- Feeling valued (not just processed)
- Reliability (everything works, every time)

The Bottom Line

Five-star reviews don't come from five-star fixtures. They come from a guest journey that makes someone feel like they made the right choice.

Dubai's best STR operators obsess over this. The ones struggling are still focused on marble countertops.

If your reviews aren't where you want them to be, audit your guest journey. The answer is there.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "7 min read"
  },
  {
    slug: "dynamic-pricing-strategies",
    image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800",
    category: "Revenue Strategy",
    title: "Dynamic Pricing Strategies That Actually Work",
    excerpt: "Real-world pricing tactics from Dubai's most profitable short-term rental operators.",
    metaTitle: "Why Your Pricing Tool Is Not a Pricing Strategy",
    metaDescription: "If every pricing tool disappeared tomorrow, how would you price your property? If that question makes you uncomfortable, keep reading.",
    content: `Every STR operator knows they should be using dynamic pricing. Most are doing it wrong.

The problem isn't the concept — it's the execution. Operators buy a tool, connect it to their calendar, and assume the algorithm will handle everything. Then they wonder why their revenue hasn't improved.

Here's what actually works.

Dynamic Pricing Is Not a Silver Bullet

Let's be clear: dynamic pricing software is useful. But it's only valuable if your fundamentals are right.

If your listing:
- Has poor photography
- Ranks low in search results
- Converts poorly because of weak copy
- Has mediocre reviews

...then optimizing your price won't help. You're just adjusting the cost of something nobody wants to book.

The operators seeing real results from dynamic pricing already have:
- Strong listing performance (high search ranking)
- Professional presentation (photos, copy, amenities)
- Solid reviews (4.7+ average)
- Accurate calendar management

Only then does pricing optimization move the needle.

What Dynamic Pricing Actually Does

At its core, dynamic pricing adjusts your rates based on:
- Demand forecasts (events, holidays, seasonality)
- Competitor pricing in your area
- Your booking velocity (how fast your dates are filling)
- Historical occupancy patterns

The goal is to maximize revenue — not bookings. That's a critical distinction.

A tool might recommend raising your price during low season because your property is desirable enough to still book at a premium. Or it might lower your price during high season if you're priced out of the market.

Manual pricing can't keep up with this. Dynamic pricing can.

But only if you're using it right.

The Mistakes Operators Make

Mistake #1: Over-optimizing for occupancy

Occupancy rate is a vanity metric. What matters is RevPAR (revenue per available room). A property at 60% occupancy with a higher nightly rate can outperform one at 90% occupancy with rock-bottom pricing.

Mistake #2: Trusting the algorithm blindly

Dynamic pricing tools use data, but they don't know your property like you do. If there's construction next door, a lift outage, or a quality issue, the algorithm doesn't account for that.

You still need to manually override pricing when circumstances change.

Mistake #3: Not setting proper floors and ceilings

If you let the tool price too low, you devalue your property and attract the wrong guests. If you let it price too high, you'll sit empty while competitors book out.

Set realistic boundaries:
- Minimum nightly rate (below this, it's not worth your costs)
- Maximum nightly rate (above this, you're priced out of your segment)

Mistake #4: Ignoring your competition

Dynamic pricing adjusts based on market rates. But if your competitors are also using dynamic pricing tools, you end up in a race to the bottom.

The solution? Differentiate on experience, not price.

The Real Strategy

The most successful operators in Dubai don't just use dynamic pricing — they layer it into a broader revenue strategy.

Here's what they do:

1. Optimize for search ranking first
Before worrying about price, get your listing visible. Ranking on page 1 of search results is more valuable than perfect pricing on page 3.

2. Segment your pricing
Not every night has the same value. Weekends, holidays, events, and corporate booking seasons all behave differently. Tailor your pricing strategy by segment.

3. Incentivize longer stays
Offer discounts for weekly and monthly bookings. These stabilize cash flow and reduce turnover costs.

4. Test and refine
Dynamic pricing tools give you data. Use it. Track which price points drive bookings vs. which ones don't convert. Adjust your strategy accordingly.

5. Stay competitive without undercutting
Know what comparable properties charge. Price below them if you're newer or have fewer reviews. Match them once you're established. Price above them only if you genuinely offer more value.

When to Override the Algorithm

Even with a tool managing your pricing, there are times to step in:
- You have a maintenance issue affecting guest experience
- A major event happens that the algorithm hasn't priced for yet
- Your reviews dip and you need to rebuild trust
- You're testing a new strategy (e.g., attracting longer stays)

Good operators don't set-and-forget. They monitor, adjust, and iterate.

Should You Invest in a Tool?

If your property is:
- Well-presented
- Ranking well in search
- Getting consistent bookings
- Generating solid reviews

...then yes, dynamic pricing software is worth it. It'll save you time and likely increase revenue by 10-20%.

But if your fundamentals aren't right, fix those first. The best pricing strategy in the world can't save a poorly optimized listing.

The Bottom Line

Dynamic pricing works — but only as part of a complete revenue strategy. The operators making serious money aren't just using tools. They're combining smart pricing with strong listings, great guest experiences, and relentless optimization.

If you're serious about maximizing revenue, start with the fundamentals. Then layer in dynamic pricing once you've built something worth pricing dynamically.`,
    author: "Stephanie Williams",
    published_date: new Date().toISOString(),
    read_time: "6 min read"
  }
];

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      // Try to fetch from API first
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
        setLoading(false);
        return;
      }
      
      // Fallback to default posts if API fails
      const fallbackPost = defaultBlogPosts.find(p => p.slug === slug);
      if (fallbackPost) {
        setPost(fallbackPost);
        setLoading(false);
      } else {
        throw new Error('Post not found');
      }
    } catch (err) {
      // Fallback to default posts
      const fallbackPost = defaultBlogPosts.find(p => p.slug === slug);
      if (fallbackPost) {
        setPost(fallbackPost);
        setLoading(false);
      } else {
        setError(err.message);
        setLoading(false);
      }
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F5F0E8',
        fontFamily: 'DM Sans, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '32px', color: '#2C1F14' }}>
            Loading...
          </h2>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F5F0E8',
        fontFamily: 'DM Sans, sans-serif',
        padding: '40px 20px'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '600px' }}>
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '36px', color: '#2C1F14', marginBottom: '20px' }}>
            Blog Post Not Found
          </h2>
          <p style={{ color: '#6B5744', marginBottom: '30px', fontSize: '16px' }}>
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Link 
            to="/"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              background: '#2C1F14',
              color: '#F5F0E8',
              textDecoration: 'none',
              fontSize: '11px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontWeight: 500
            }}
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={post.metaTitle || post.title}
        description={post.metaDescription || post.excerpt}
        canonical={`https://stephaniesluxesolutions.com/blog/${slug}`}
        ogImage={post.image}
        type="article"
      />
      <div style={{ background: '#F5F0E8', minHeight: '100vh' }}>
      {/* Header */}
      <nav style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#F5F0E8',
        borderBottom: '0.5px solid #DDD4C0',
        padding: '16px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontSize: '22px',
            fontWeight: 400,
            color: '#2C1F14',
            letterSpacing: '0.04em'
          }}>
            <span style={{ opacity: 0.2 }}>S</span>L<span style={{ opacity: 0.2 }}>S</span>
          </div>
        </Link>
        <Link 
          to="/#blog"
          style={{
            fontSize: '10.5px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#6B5744',
            textDecoration: 'none'
          }}
        >
          ← Back to Blog
        </Link>
      </nav>

      {/* Hero Image */}
      <div style={{ width: '100%', height: '400px', overflow: 'hidden', background: '#EDE5D8' }}>
        <img 
          src={post.image} 
          alt={post.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Content */}
      <article style={{ maxWidth: '800px', margin: '0 auto', padding: '60px 40px' }}>
        {/* Category Badge */}
        <div style={{
          fontSize: '9px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: '#C4A882',
          marginBottom: '20px'
        }}>
          {post.category}
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '48px',
          fontWeight: 300,
          color: '#2C1F14',
          lineHeight: 1.2,
          marginBottom: '20px'
        }}>
          {post.title}
        </h1>

        {/* Meta Info */}
        <div style={{
          display: 'flex',
          gap: '20px',
          fontSize: '13px',
          color: '#6B5744',
          marginBottom: '40px',
          paddingBottom: '30px',
          borderBottom: '0.5px solid #DDD4C0'
        }}>
          <span>{post.author}</span>
          <span>•</span>
          <span>{new Date(post.published_date).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
          <span>•</span>
          <span>{post.read_time}</span>
        </div>

        {/* Excerpt */}
        <p style={{
          fontSize: '18px',
          color: '#6B5744',
          lineHeight: 1.8,
          fontWeight: 300,
          marginBottom: '40px',
          fontStyle: 'italic'
        }}>
          {post.excerpt}
        </p>

        {/* Content */}
        <div 
          style={{
            fontSize: '16px',
            color: '#2C1F14',
            lineHeight: 1.9,
            fontWeight: 300
          }}
          dangerouslySetInnerHTML={{ __html: post.content.replace(/\n/g, '<br/><br/>') }}
        />

        {/* Back to Blog */}
        <div style={{ marginTop: '60px', paddingTop: '40px', borderTop: '0.5px solid #DDD4C0' }}>
          <Link 
            to="/#blog"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              background: '#2C1F14',
              color: '#F5F0E8',
              textDecoration: 'none',
              fontSize: '11px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              fontWeight: 500
            }}
          >
            ← Back to All Posts
          </Link>
        </div>
      </article>

      {/* Footer */}
      <footer style={{
        background: '#221508',
        padding: '28px 48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: '60px',
        color: '#9B7F5F'
      }}>
        <div style={{ fontSize: '7.5px', letterSpacing: '0.24em', textTransform: 'uppercase' }}>
          Stephanies Luxe Solutions
        </div>
        <div style={{ fontSize: '11px' }}>
          © 2025 · All rights reserved · UAE
        </div>
      </footer>
      </div>
    </>
  );
};

export default BlogPost;